import { z } from "zod";

import type { ChatTableProps } from "@/components/ui/chat/table/ChatTable";
import ChatTable from "@/components/ui/chat/table/ChatTable";

/** Catalog definition for Table (ChatTable). Use in renderer catalog and chat catalog. */
export const ChatTableSpec = {
  props: z.object({
    columns: z.array(z.string()),
    rows: z.array(z.array(z.string())).nullable(),
    title: z.string().nullable(),
    subtitle: z.string().nullable(),
    caption: z.string().nullable(),
    data: z.array(z.record(z.string(), z.unknown())).nullable(),
    sortKey: z.string().nullable(),
    sortDir: z.enum(["asc", "desc"]).nullable(),
    emptyMessage: z.string().nullable(),
  }),
  description:
    "Place Table as a direct child of the root Stack or inside Card (shares the Card chrome and export surface). Avoid unrelated Callout, HorizontalBar, or charts in the same Card as the table unless one section. Use Table for article lists. Set title or caption (bold heading; title wins if both). Optional subtitle (muted line under heading, e.g. date range). Provide columns + data (record[]) with numeric fields for sorting. Column headers are clickable to sort. Links in cells: use { text, url } for one link; for several links in one cell (e.g. evidence or sources), use an array of { text, url } objects, which renders as comma-separated links. Never put raw URLs in cells. Sentiment / polarity columns: cells are plain strings, but they MUST follow the same semantics as the catalog Badge component (@json-render/shadcn): map positive → default, negative → destructive, neutral → secondary (use cell text such as Positive, Negative, Neutral, or supportive/critical/mixed). Name columns clearly (e.g. Sentiment, Stance) so those cells get the matching pill styling — you cannot nest a Badge element inside a table cell in the spec; use string values that align with Badge variants.",
  example: {
    columns: ["Headline", "Publisher", "Articles", "Sentiment"],
    data: [
      {
        Headline: "Example headline here",
        Publisher: "Reuters",
        Articles: 42,
        Sentiment: "Positive",
      },
      {
        Headline: "Another article title",
        Publisher: "AP News",
        Articles: 128,
        Sentiment: "Neutral",
      },
      {
        Headline: "Breaking: Market update",
        Publisher: "Bloomberg",
        Articles: 7,
        Sentiment: "Negative",
      },
    ],
    title: "Top Publishers Driving the Narrative",
    subtitle: "By article volume, last 30 days",
  },
} as const;

/**
 * Adapter for json-render defineRegistry: forwards props to ChatTable. Full width so it takes a row in chat flex layout.
 * Note: Type cast to ChatTableProps is intentional; schema/component mismatches are not caught at compile time.
 */
export const ChatTableFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => (
  <div className="w-full">
    <ChatTable {...(props as unknown as ChatTableProps)} />
  </div>
);
